/**
 * Plain text from a stored HTML fragment, for TEXT bindings only — a Label, `{{ }}`, a page
 * title. Never feed the result to `[innerHTML]`: it decodes `&lt;` back into `<`.
 *
 * Exists for post subjects. The web composer's subject field is the same rich input as the
 * body, so what it saves is HTML-escaped — "Guitar Cover &amp; Tone Master", "CENTS.&nbsp;".
 * The web's post detail renders the subject as HTML and reads fine; every place that shows it
 * as text (the web's feed rows and tab title, every mobile surface) showed the entities
 * literally. Decoding at display covers the posts already stored, and leaves the stored value
 * escaped, which is what that `[innerHTML]` needs.
 *
 * Tags are dropped, the common named entities and all numeric ones decoded, and whitespace
 * runs collapsed. `&amp;` is decoded LAST, so an escaped entity ("&amp;lt;") comes out as the
 * literal text the author typed ("&lt;"), not as "<".
 */
export function htmlFragmentToText(value: unknown): string {
  return `${value ?? ""}`
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&#(\d+);/g, (match, code) => fromCodePoint(Number(code), match))
    .replace(/&#x([0-9a-f]+);/gi, (match, code) => fromCodePoint(parseInt(code, 16), match))
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
}

function fromCodePoint(code: number, fallback: string): string {
  try {
    return String.fromCodePoint(code);
  } catch {
    return fallback;
  }
}
